import { createReducer } from '@reduxjs/toolkit';
import { BatchDto, GoodsDto, OrderDto, OrderStatus, PurchaseOrderDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyGoodsBatchEntityForPurchaseOrder,
    clearGoodsForPurchaseOrder,
    updateGoodsArticleForPurchaseOrder,
    updateGoodsQuantityForPurchaseOrder,
    updateGoodsSiteForPurchaseOrder,
    updateGoodsSiteIdForPurchaseOrder,
    updateGoodsStatusForPurchaseOrder,
    updateBatchArrivalTimeForPurchaseOrder,
    updateBatchClientForPurchaseOrder,
    updateBatchPriceForPurchaseOrder,
    updateBatchUniqueIdForPurchaseOrder,
    removeGoodsBatchEntityForPurchaseOrder,
    setGoodsBatchEntitiesForPurchaseOrder,
} from '../actions/purchaseOrderActions';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';
import { GoodBatchEntity } from './goodsToSiteFormReducer';

export interface GoodsToPurchaseOrderFormReducerState extends IBaseEntityState {
    goodBatchEntities: GoodBatchEntity[] | null;
}

function clearState(state: GoodsToPurchaseOrderFormReducerState): void {
    state.goodBatchEntities = null;

    clearBaseEntityState(state);
}

function getInitialState(): GoodsToPurchaseOrderFormReducerState {
    const state: GoodsToPurchaseOrderFormReducerState = {
        goodBatchEntities: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const goodsToPurchaseOrderFormReducer = createReducer<GoodsToPurchaseOrderFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(
                setGoodsBatchEntitiesForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    const { goods, receptionOrders }: { goods: GoodsDto[]; receptionOrders: OrderDto[] } =
                        action.payload;

                    const newGoodsForOrder: GoodBatchEntity[] = [];

                    const deliveredGoodsItems = receptionOrders
                        ?.filter((ro) => ro.orderStatus === OrderStatus.Delivered)
                        ?.map((ro) => ro.orderGoodsItems ?? [])
                        .flat();

                    goods.map((g) => {
                        const deliveredGoodsForArticle = deliveredGoodsItems?.filter(
                            (ogi) => ogi.articleId === g.article?.id,
                        );

                        const deliveredQuantity = deliveredGoodsForArticle?.reduce((acc, val) => {
                            return (acc += val.quantity ?? 0);
                        }, 0);

                        const newGoods = new GoodsDto();
                        newGoods.init(g);
                        newGoods.quantity = newGoods.quantity ? newGoods.quantity - deliveredQuantity : 0;

                        const newGoodBatch: GoodBatchEntity = {
                            index: !newGoodsForOrder || newGoodsForOrder.length <= 0 ? 1 : newGoodsForOrder.length + 1,
                            batch: g.batch ?? new BatchDto({ batchArrivalTime: new Date() }),
                            goods: newGoods,
                        };

                        newGoodsForOrder.unshift(newGoodBatch);
                    });

                    state.goodBatchEntities = newGoodsForOrder;
                },
            )
            .addCase(
                createNewEmptyGoodsBatchEntityForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    const index =
                        !!state.goodBatchEntities && state.goodBatchEntities.length > 0
                            ? state.goodBatchEntities[state.goodBatchEntities.length - 1].index + 1
                            : 1;

                    const newGoodBatch: GoodBatchEntity = {
                        index: index,
                        batch: new BatchDto({ batchArrivalTime: new Date() }),
                        goods: new GoodsDto(),
                    };

                    if (!state.goodBatchEntities) {
                        state.goodBatchEntities = [newGoodBatch];
                    } else {
                        state.goodBatchEntities = [...state.goodBatchEntities, newGoodBatch];
                    }
                },
            )
            .addCase(clearGoodsForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(
                removeGoodsBatchEntityForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    if (!!state.goodBatchEntities) {
                        const index = action.payload;
                        if (state.goodBatchEntities.length === 0) {
                            state.goodBatchEntities = [];
                        } else {
                            const goodIndex = state.goodBatchEntities.findIndex((g) => g.index === index);
                            if (goodIndex > -1) {
                                state.goodBatchEntities.splice(goodIndex, 1);
                            }
                        }
                    }
                },
            )
            .addCase(updateGoodsArticleForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, article } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.article = article;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsStatusForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, goodsStatus } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.goodsStatus = goodsStatus;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(
                updateGoodsQuantityForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                    const { index, quantity } = action.payload;

                    const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                    if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                        const newGood = state.goodBatchEntities[newGoodIndex];

                        newGood.goods.quantity = quantity;

                        state.goodBatchEntities = [
                            ...state.goodBatchEntities.slice(0, newGoodIndex),
                            newGood,
                            ...state.goodBatchEntities.slice(newGoodIndex + 1),
                        ];
                    }
                },
            )
            .addCase(updateGoodsSiteIdForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, siteId } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.siteId = siteId;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsSiteForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, site } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.site = site;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateBatchPriceForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, batchPrice } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.purchasePricePerArticle = batchPrice;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(
                updateBatchUniqueIdForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                    const { index, uniqueBatchId } = action.payload;

                    const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                    if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                        const newGood = state.goodBatchEntities[newGoodIndex];

                        newGood.batch.uniqueBatchId = uniqueBatchId;

                        state.goodBatchEntities = [
                            ...state.goodBatchEntities.slice(0, newGoodIndex),
                            newGood,
                            ...state.goodBatchEntities.slice(newGoodIndex + 1),
                        ];
                    }
                },
            )
            .addCase(updateBatchClientForPurchaseOrder, (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, batchClient } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.supplier = batchClient;
                    newGood.batch.supplierId = batchClient?.id;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(
                updateBatchArrivalTimeForPurchaseOrder,
                (state: GoodsToPurchaseOrderFormReducerState, action: any) => {
                    myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                    const { index, batchArrivalTime } = action.payload;

                    const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                    if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                        const newGood = state.goodBatchEntities[newGoodIndex];

                        newGood.batch.batchArrivalTime = batchArrivalTime;

                        state.goodBatchEntities = [
                            ...state.goodBatchEntities.slice(0, newGoodIndex),
                            newGood,
                            ...state.goodBatchEntities.slice(newGoodIndex + 1),
                        ];
                    }
                },
            );
    },
);
